'use client';

import { usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import React, { FC, SyntheticEvent, useEffect, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import Aspect11 from '@/public/icons/aspect-1-1.svg';
import Aspect169 from '@/public/icons/aspect-16-9.svg';
import Aspect45 from '@/public/icons/aspect-4-5.svg';
import Aspect916 from '@/public/icons/aspect-9-16.svg';
import { useStyleContext } from '@/src/providers';

const styles = {
  wrap: 'flex flex-col gap-2 p-4 bg-fill-quinary rounded-xl',
  head: 'flex items-center justify-between py-1',
  label: 'text-sm font-medium',
  range: 'w-full h-2 bg-fill-rose rounded-full appearance-none cursor-pointer',
  buttonGroup: 'grid grid-cols-2 grid-rows-2 gap-2 mb-4',
  button:
    'px-2 py-2 bg-fill-quinary rounded-lg w-full cursor-pointer transition-all flex font-medium items-center gap-3 text-sm text-label-secondary',
  buttonActive: 'bg-fill-rose text-label-primary',
  icon: 'opacity-40',
  iconActive: 'opacity-100',
};

const AspectRatioButton = ({
  value,
  text,
  icon,
  isHidden = false,
}: {
  value: string;
  text: string;
  icon: JSX.Element;
  isHidden?: boolean;
  onClick?: () => void;
}) => {
  const { activeAspectRatio, setActiveAspectRatio } = useStyleContext();

  const handleChange = (event: SyntheticEvent) => {
    setActiveAspectRatio(value);
  };

  if (isHidden) {
    return null;
  }

  return (
    <button
      key={value}
      className={twMerge(
        styles.button,
        activeAspectRatio === value && styles.buttonActive,
      )}
      onClick={handleChange}
    >
      {icon}
      {text}
    </button>
  );
};

export const AspectRatio: FC = () => {
  const t = useTranslations('imageModal.aspectRatio');
  const { activeAspectRatio, setActiveAspectRatio, activeStyle } =
    useStyleContext();
  const { currentPortal } = usePortalContext();

  const values = [
    {
      value: '16:9',
      text: t('16-9'),
      icon: (
        <Aspect169
          className={twMerge(
            styles.icon,
            activeAspectRatio === '16:9' && styles.iconActive,
          )}
        />
      ),
    },
    {
      value: '1:1',
      text: t('1-1'),
      icon: (
        <Aspect11
          className={twMerge(
            styles.icon,
            activeAspectRatio === '1:1' && styles.iconActive,
          )}
        />
      ),
    },
    {
      value: '4:5',
      text: t('4-5'),
      isHidden:
        ['VideoLook', 'VideoStyle', 'CombiVideo'].includes(
          activeStyle?.__typename!,
        ) || currentPortal === PORTALS.AnyImageToVideo,
      icon: (
        <Aspect45
          className={twMerge(
            styles.icon,
            activeAspectRatio === '4:5' && styles.iconActive,
          )}
        />
      ),
    },
    {
      value: '9:16',
      text: t('9-16'),
      icon: (
        <Aspect916
          className={twMerge(
            styles.icon,
            activeAspectRatio === '9:16' && styles.iconActive,
          )}
        />
      ),
    },
  ];

  useEffect(() => {
    if (
      (['VideoLook', 'VideoStyle', 'CombiVideo'].includes(
        activeStyle?.__typename!,
      ) ||
        currentPortal === PORTALS.AnyImageToVideo) &&
      activeAspectRatio === '4:5'
    ) {
      setActiveAspectRatio('1:1');
    }
  }, [activeStyle, activeAspectRatio, setActiveAspectRatio, currentPortal]);

  return (
    <div className={styles.buttonGroup}>
      {values.map((aspectRatio, index) => (
        <AspectRatioButton {...aspectRatio} key={aspectRatio.text} />
      ))}
    </div>
  );
};
